import React, { useState, useEffect, useRef } from "react";
import { graphql } from "gatsby";
import getYouTubeID from "get-youtube-id";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import { cn, toPlainText, mapEdgesToNodes, getInviteDateLabels } from "../lib/helpers";
import { GetStarted, LogoCarousel, SectionWrapper } from "../components/sections";
import { BackToZone } from "../components/back-to-zone";
import PortableText from "../components/portableText";
import { Page as PageSections } from "../components/page";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faClock, faLocationDot, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Byline } from "../components/card/byline";
import { CardContext } from "../components/card/context";
import { SectionContext } from "../components/sections/context";
import { PillHeader } from "../components/sections/wrapper";
import { Pill } from "../components/card/pills";
import { ImageBuilder } from "../components/global/image-builder";
import { ExternalVideo } from "../components/serializers-video";
import { PlayIcon } from "../components/icon/play";
import { EmailSignup } from "../components/global/email-signup";
import { DiamondPanel } from "../components/global/diamond-panel";
import * as styles from "../components/global/global.module.css";

export const EVENT_TYPE_LABELS = {
  online: "Webinar",
  "in-person": "In-person",
  "on-demand": "On-demand",
};

export const query = graphql`
  query InviteTemplateQuery($id: String!) {
    doc: sanityInvite(id: { eq: $id }) {
      _type
      id
      _id
      title
      slug {
        current
      }
      status
      startDate
      endDate
      allDay
      recurring
      url
      eventType
      location
      extraFormFields
      mainVideo {
        ...externalVideo
      }
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      _rawBody(resolveReferences: { maxDepth: 12 })
      mainImage {
        ...SanityImage
        alt
      }
      backgroundImage {
        ...SanityImageSimple
      }
      authors {
        ... on SanityAuthorReference {
          _key
          _type
          author {
            ...SanityAuthor
          }
        }
        ... on SanitySpeakerName {
          _key
          _type
          name
        }
      }
      sections {
        ... on SanityPageComponent {
          _type
          name
          anchor
          header
          _rawSummary(resolveReferences: { maxDepth: 5 })
          layout
          background
          padding
          container
          mainImage {
            ...SanityImageSimple
          }
          image {
            ...SanityImage
            alt
            caption
            link
          }
          category
          effect
          cards
          types
          contentRefs {
            ... on SanityPlaybook {
              _id
              _type
              title
              slug {
                current
              }
              _rawExcerpt(resolveReferences: { maxDepth: 5 })
              authors {
                _key
                author {
                  ...SanityAuthor
                }
              }
            }
            ... on SanityQuotable {
              ...Quotable
            }
          }
        }
        ... on SanityPageSectionSimple {
          _type
          _rawContent(resolveReferences: { maxDepth: 5 })
          anchor
          header
          _rawSummary(resolveReferences: { maxDepth: 5 })
          layout
          background
          padding
          container
          mainImage {
            ...SanityImageSimple
          }
          image {
            ...SanityImage
            alt
            caption
            link
          }
          tableOfContents
          rounded
          constrainImages
        }
        ... on SanityPageSectionColumns {
          _type
          content {
            header
            mainImage {
              ...SanityImageSimple
            }
            _rawContent(resolveReferences: { maxDepth: 5 })
            author {
              ...SanityAuthor
            }
            layout
            icon
            imageAlt
          }
          maxColumns
          anchor
          header
          _rawSummary(resolveReferences: { maxDepth: 5 })
          layout
          background
          padding
          style
          container
          image {
            ...SanityImage
          }
        }
      }
    }
    recordingPosts: allSanityPost(
      limit: 50
      sort: { fields: [publishedAt], order: [DESC] }
      filter: { status: { eq: "published" } }
    ) {
      edges {
        node {
          ...cardNode
          _rawBody(resolveReferences: { maxDepth: 5 })
        }
      }
    }
    recordingStories: allSanityStory(
      limit: 50
      sort: { fields: [publishedAt], order: [DESC] }
      filter: { status: { eq: "published" } }
    ) {
      edges {
        node {
          ...cardNodeStory
          _rawBody(resolveReferences: { maxDepth: 5 })
        }
      }
    }
    recordingVideos: allSanityVideo(
      limit: 12
      sort: { fields: [_createdAt], order: [DESC] }
      filter: { status: { eq: "published" }, slug: { current: { ne: null } } }
    ) {
      edges {
        node {
          ...cardNodeVideo
        }
      }
    }
  }
`;

const InviteTemplate = ({ data }) => (
  <LayoutContainer
    doc={{ ...data?.doc, backgroundColor: "lightGreen" }}
    hideNav
    darkBg={!!data?.doc?.backgroundImage?.asset}
  >
    <PageHeadMeta
      title={data?.doc.title}
      description={toPlainText(data?.doc._rawExcerpt)}
      image={data?.doc.mainImage}
      doc={data?.doc}
    />
    {data?.doc && (
      <InvitePage
        doc={data.doc}
        recordings={[]
          .concat(mapEdgesToNodes(data.recordingPosts))
          .concat(mapEdgesToNodes(data.recordingStories))
          .concat(mapEdgesToNodes(data.recordingVideos))
          .filter((node) =>
            node.mainVideo?.url ||
            node.mainVideo?.vimeoId ||
            node._rawBody?.some((b) => b._type === "externalVideo" && (b.url || b.vimeoId || b.loomUrl))
          )
          .sort((a, b) => new Date(b.publishedAt || b._createdAt) - new Date(a.publishedAt || a._createdAt))
        }
      />
    )}
  </LayoutContainer>
);

export default InviteTemplate;

const Countdown = ({ targetDate }) => {
  const calculate = () => {
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculate);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculate()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!timeLeft) return null;

  return (
    <div className="flex gap-8">
      {[
        { label: "Days", value: timeLeft.days },
        { label: "Hours", value: timeLeft.hours },
        { label: "Minutes", value: timeLeft.minutes },
        { label: "Seconds", value: timeLeft.seconds },
      ].map(({ label, value }) => (
        <div key={label} className="text-center">
          <div className="text-4xl tabular-nums leading-none">
            {String(value).padStart(2, "0")}
          </div>
          <div className="text-sm text-secondary uppercase tracking-wide mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
};

const OnDemandPlayButton = ({ mainVideo, onPlay }) => {
  const thumbnailSrc = !mainVideo.previewImage && mainVideo.url
    ? `https://img.youtube.com/vi/${getYouTubeID(mainVideo.url)}/hqdefault.jpg`
    : null;

  return (
    <button
      type="button"
      onClick={onPlay}
      className="relative block w-full rounded-2xl overflow-hidden group bg-dark-5"
      style={{ paddingBottom: "56.25%", height: 0 }}
    >
      <ImageBuilder
        image={mainVideo.previewImage}
        src={thumbnailSrc}
        alt="Watch the recording"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-dark-20 group-hover:bg-dark-30 transition-colors">
        <div className="bg-green-emerald rounded-full p-1 shadow-lg transform transition-transform group-hover:scale-110">
          <PlayIcon size="56" />
        </div>
      </div>
    </button>
  );
};

const HeroDetails = ({ dark, eventType, title, ondemandMode, recurring, dateLabel, timeLabel, location, rawExcerpt, authors }) => (
  <>
    {!dark && <Pill label={EVENT_TYPE_LABELS[eventType] || "Webinar"} />}
    <h1 className={cn(
      "text-5xl md:text-7xl font-semibold mb-6 mt-12",
      ondemandMode ? "md:mt-4" : "mx-4 md:mx-12 md:mt-0",
      dark ? "text-white" : null)
    }>
      {title}
    </h1>
    {rawExcerpt && (
      <div className={cn("my-6 md:my-10 text-lg md:text-xl leading-snug", dark ? "text-light-80" : "text-dark-60")}>
        <PortableText blocks={rawExcerpt} />
      </div>
    )}
    <ul className={cn("max-w-2/3 mx-auto sm:max-w-full flex flex-col gap-2 text-xl font-thin mt-8", dark ? "text-white" : "text-secondary")}>
      {location && (
        <li className="flex items-center justify-center gap-2">
          <FontAwesomeIcon icon={faLocationDot} className={cn("hidden sm:inline w-4 text-green-emerald", dark ? null : "opacity-80")} />
          <span>{location}</span>
        </li>
      )}
      {!ondemandMode && recurring && <li>Recurring event</li>}
      {!ondemandMode && dateLabel && (
        <li className="flex items-center justify-center gap-2">
          <FontAwesomeIcon icon={faCalendarDays} className={cn("hidden sm:inline w-4 text-green-emerald", dark ? null : "opacity-80")} />
          <span>{dateLabel}</span>
        </li>
      )}
      {!ondemandMode && timeLabel && (
        <li className="flex items-center justify-center gap-2">
          <FontAwesomeIcon icon={faClock} className={cn("hidden sm:inline w-4 text-green-emerald", dark ? null : "opacity-80")} />
          <span>{timeLabel}</span>
        </li>
      )}
    </ul>
    {authors.length > 0 && (
      <div className="mt-4 md:mt-8">
        <h4 className={cn("mb-4 uppercase", dark ? "text-white" : null)}>Speakers & Hosts</h4>
        <SectionContext.Provider value={{ isDarkBg: dark }}>
          <CardContext.Provider value={{ authors }}>
            <Byline hideBorder />
          </CardContext.Provider>
        </SectionContext.Provider>
      </div>
    )}
  </>
);

const InvitePage = ({ doc, recordings = [] }) => {
  const { title, startDate, endDate, allDay, recurring, location, eventType, mainVideo, _rawExcerpt, _rawBody, authors, extraFormFields, sections, backgroundImage } = doc;

  const ondemandMode = eventType === "on-demand" && mainVideo;
  const fancyMode = !!backgroundImage?.asset;

  const { dateLabel, timeLabel } = recurring
    ? { dateLabel: null, timeLabel: null }
    : getInviteDateLabels({ startDate, endDate, allDay });

  const speakerNames = (authors || []).map((a) =>
    a._type === "speakerName" ? a.name : a.author?.name
  ).filter(Boolean);

  const [videoOpen, setVideoOpen] = useState(false);
  const [registered, setRegistered] = useState(false);
  const playerSectionRef = useRef();

  useEffect(() => {
    if (videoOpen && playerSectionRef.current) {
      const navHeight = document.getElementById("nav")?.getBoundingClientRect().height || 0;
      const top = playerSectionRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 16;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [videoOpen]);

  const bodyBlock = _rawBody && <PortableText blocks={_rawBody} className="max-w-2xl mt-12" />;

  const registrationSlot = ondemandMode ? (
    <OnDemandPlayButton mainVideo={mainVideo} onPlay={() => setVideoOpen(true)} />
  ) : registered ? (
    <p className="text-center py-8 font-normal text-2xl">You're registered!</p>
  ) : (
    <div className="relative">
      <EmailSignup
        buttonText="Register"
        supressMessage={true}
        placeholder="Work email"
        domId="event-registration"
        hubspotSubmit={true}
        inputClasses="py-3"
        attribution={{
          website_attribution: "event-registration",
          action: "event-registration",
          label: "email",
        }}
        wrapperClasses="border-2 border-white"
        buttonClasses="bg-dark-80 hover:bg-dark-60 text-white"
        baseFormFields={true}
        extraFormFields={extraFormFields}
        toggle={() => setRegistered(true)}
      />
    </div>
  );

  return (
    <>
      {videoOpen && ondemandMode && (
        <SectionWrapper padding="top">
          <div ref={playerSectionRef}>
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              aria-label="Close video"
              className="flex items-center gap-2 mb-6 opacity-70 hover:opacity-100 transition-opacity"
            >
              <FontAwesomeIcon icon={faXmark} />
              Close
            </button>
            <ExternalVideo node={mainVideo} inline />
          </div>
        </SectionWrapper>
      )}
      {fancyMode ? (
        <SectionWrapper padding="none" className="relative overflow-hidden">
          <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-screen overflow-hidden transform z-0">
            <ImageBuilder width={2400} image={backgroundImage} alt="" className={cn("w-full h-full object-cover", styles.kenBurns)} />
          </div>
          <div className="relative z-10 pb-12 md:py-12">
            <div className="w-full md:w-2/3 mx-auto pt-24 md:pt-0">
              <DiamondPanel>
                <HeroDetails
                  dark
                  eventType={eventType}
                  title={title}
                  ondemandMode={ondemandMode}
                  recurring={recurring}
                  dateLabel={dateLabel}
                  timeLabel={timeLabel}
                  location={location}
                  rawExcerpt={_rawExcerpt}
                  authors={authors}
                />
              </DiamondPanel>
            </div>
            <div className="text-center mt-24 md:absolute md:left-0 md:bottom-12">
              <BackToZone to="/events/" text="See all events" darkBg />
            </div>
          </div>
        </SectionWrapper>
      ) : (
        <SectionWrapper>
          <div className="grid lg:grid-cols-3/5-2/5 gap-12 lg:gap-32 items-start">
            <div>
              <BackToZone to="/events/" text="See all events" />
              <HeroDetails
                dark={false}
                eventType={eventType}
                title={title}
                ondemandMode={ondemandMode}
                recurring={recurring}
                dateLabel={dateLabel}
                timeLabel={timeLabel}
                location={location}
                rawExcerpt={_rawExcerpt}
                authors={authors}
              />
              {bodyBlock}
            </div>
            <div className="lg:mt-16 relative">
              <div
                className="absolute inset-0 -m-32 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at center, rgba(27,200,140,.2) 0%, transparent 70%)" }}
              />
              {registrationSlot}
            </div>
          </div>
        </SectionWrapper>
      )}

      {sections?.length > 0 && (
        <PageSections sections={sections} title={title} slug={doc.slug} />
      )}

      {(fancyMode && !doc.url) && (
        <SectionWrapper background="lavenderUltraLight">
          <div className="grid lg:grid-cols-3/5-2/5 gap-12 lg:gap-32 items-start">
            <div>{bodyBlock}</div>
            <div className="lg:mt-16">{registrationSlot}</div>
          </div>
        </SectionWrapper>
      )}

      {!fancyMode && (
        <SectionWrapper container="full" padding="tight" background="lavenderUltraLight">
          <h4 className="mb-4 mt-12 uppercase text-center">Trusted by teams at</h4>
          <LogoCarousel />
        </SectionWrapper>
      )}

      <SectionWrapper
        background="mint"
        name="getStarted"
      >
        <div className="text-center">
          <PillHeader header="Turn every signal into pipeline." background="mint" />
          <h5 className="text-2xl leading-normal my-6 max-w-4xl mx-auto">
            Get a personalized walkthrough of Common Room and see how top GTM teams uncover, prioritize, and convert their next best customers.
          </h5>
          <GetStarted section={{ layout: "centered" }} />
        </div>
      </SectionWrapper>

      {/* {recordings.length > 0 && (
        <SectionWrapper header="On-demand recordings" background="lightGreen">
          <RecordingPlaylist recordings={recordings} />
        </SectionWrapper>
      )} */}
    </>
  );
};
